import { useCallback, useEffect, useRef, useState } from "react";
import { detectFps, openVideo } from "../../video/upscale/process";
import { MAX_FILE_BYTES } from "../../video/upscale/plan";

// What the in-browser video tools (upscaling, noise reduction) have in common: opening the visitor's video, running a job on it (with progress, a live picture and a Stop button),
// and keeping the finished videos until the page is left.
export default function useVideoFx() {
  const [file, setFile] = useState(null);
  const [opened, setOpened] = useState(null);                 // { video, url, w, h, seconds }
  const [fps, setFps] = useState(0);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);                     // { progress, startedAt }
  const [results, setResults] = useState([]);
  const cancelRef = useRef({ cancelled: false });
  const liveHost = useRef(null);                              // where on the page the picture being made is shown
  const aliveRef = useRef(true);
  const openedRef = useRef(null);
  const resultsRef = useRef([]);
  resultsRef.current = results;

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      cancelRef.current.cancelled = true;
      if (openedRef.current) { openedRef.current.video.pause(); URL.revokeObjectURL(openedRef.current.url); }
      resultsRef.current.forEach((r) => URL.revokeObjectURL(r.url));
    };
  }, []);

  const release = () => {
    if (openedRef.current) { openedRef.current.video.pause(); URL.revokeObjectURL(openedRef.current.url); }
    openedRef.current = null;
  };

  // Opens a video the visitor chose; `onOpened(next)` is called with { video, w, h, seconds } once it is ready (for example to measure it).
  const take = useCallback(async (chosen, onOpened) => {
    setError("");
    if (!chosen) return;
    if (!chosen.type.startsWith("video/") && !/\.(mp4|mov|m4v|webm|mkv)$/i.test(chosen.name)) { setError(`"${chosen.name}" is not a video file. Use an MP4, MOV or WebM file.`); return; }
    if (chosen.size > MAX_FILE_BYTES) { setError(`"${chosen.name}" is larger than 2 GB. Cut it into shorter parts first.`); return; }
    setReading(true);
    try {
      const next = await openVideo(chosen);
      if (!aliveRef.current) { URL.revokeObjectURL(next.url); return; }
      release();
      openedRef.current = next;
      setFile(chosen);
      setOpened(next);
      setFps(0);
      detectFps(next.video).then((value) => {
        if (!aliveRef.current || openedRef.current !== next) return;
        setFps(value);
        if (onOpened) onOpened(next);
      });
    } catch (e) {
      setError(e.message || "That video could not be opened.");
    }
    setReading(false);
  }, []);

  const clear = () => { release(); setOpened(null); setFile(null); };

  // The picture being made is shown while it is made: the canvas it is drawn on is put on the page itself (copying it to another canvas would make the graphics card hand over
  // every frame, which can stall a slow one).
  const show = (canvas) => {
    const host = liveHost.current;
    if (!host) return;
    canvas.style.cssText = "display:block;max-width:100%;max-height:560px;margin:0 auto;border-radius:12px;background:#000";
    host.replaceChildren(canvas);
  };

  // Runs `job({ signal, onProgress, onCanvas })`, which resolves with { result (from processVideo), ext, look, extra }; the finished video is added to the results.
  const run = async (job) => {
    if (!opened) return;
    setError("");
    opened.video.pause();
    cancelRef.current = { cancelled: false };
    const startedAt = Date.now();
    setBusy({ progress: 0, startedAt });
    try {
      const done = await job({
        signal: cancelRef.current,
        onProgress: (progress) => { if (aliveRef.current) setBusy((b) => (b ? { ...b, progress } : b)); },
        onCanvas: show,
      });
      if (!aliveRef.current) return;
      const { result } = done;
      const notes = [];
      if (result.hiddenSeconds > 1) notes.push(`This tab was in the background for about ${Math.round(result.hiddenSeconds)} s, so some frames of that part may be missing. Keep the tab open and visible next time.`);
      else if (result.presented > 30 && result.frames < result.presented * 0.85) notes.push("The computer could not keep up with this size, so some frames are missing and the motion may look uneven. Try a smaller size.");
      const item = {
        id: Date.now(), url: URL.createObjectURL(result.blob), blob: result.blob, name: file.name, w: result.w, h: result.h, inW: opened.w, inH: opened.h, seconds: result.seconds,
        size: result.blob.size, ext: done.ext, look: done.look, extra: done.extra || "", took: Math.round((Date.now() - startedAt) / 1000), notes,
      };
      setResults((list) => [item, ...list]);
    } catch (e) {
      if (e.message !== "cancelled" && aliveRef.current) setError(e.message || "The video could not be processed. Please try again.");
    }
    if (aliveRef.current) setBusy(null);
  };

  const removeResult = (item) => { URL.revokeObjectURL(item.url); setResults((list) => list.filter((r) => r.id !== item.id)); };

  return { file, opened, fps, reading, error, setError, busy, results, take, clear, run, removeResult, cancelRef, liveHost };
}
